import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-daylight-body text-sm text-ash transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  return (
    <footer className={`${parentClass || ""} border-t border-border bg-void text-foreground`}>
      <div className="daylight-container py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_2fr_0.9fr] lg:gap-16">
          <div>
            {logo ? (
              <DaylightMedia
                className="mb-7 h-9 w-9 bg-transparent"
                framed={false}
                imageClassName="object-contain object-left"
                media={logo}
                sizes="36px"
              />
            ) : null}
            {description ? (
              <DaylightRichText className="max-w-sm text-base text-ash" content={description} />
            ) : null}
            {contact ? (
              <address className="mt-6 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-daylight-mono text-xs leading-relaxed text-ash">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid grid-cols-2 gap-8 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="signal-label mb-4 text-ash">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-[8px] border border-border px-4 font-daylight-mono text-[0.6875rem] uppercase tracking-[0.1em] text-ash transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? <Icon className="size-4" name={item.icon} /> : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 font-daylight-mono text-xs text-ash sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
